/**
 * Light GA4 for the app (app.frogswork.com).
 * Override with VITE_GA4_MEASUREMENT_ID at build time if needed.
 * Never send emails, invoice content, customer names, or other PII.
 *
 * Opt out (persists on this device): visit once with ?ga_opt_out=1
 * Re-enable for testing: ?ga_opt_out=0
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
    [key: `ga-disable-${string}`]: boolean | undefined;
  }
}

const DEFAULT_MEASUREMENT_ID = "G-MV6WQ3Z369";
const MEASUREMENT_ID = String(
  import.meta.env.VITE_GA4_MEASUREMENT_ID || DEFAULT_MEASUREMENT_ID
).trim();

const OPT_OUT_KEY = "fw_ga_opt_out";
const OPT_OUT_COOKIE = "fw_ga_opt_out";
const COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 365 * 10;

let ready = false;

function canUseSharedCookie(): boolean {
  const host = location.hostname || "";
  return host === "frogswork.com" || /\.frogswork\.com$/i.test(host);
}

function readOptOutCookie(): boolean {
  return document.cookie.split(";").some((c) => c.trim().startsWith(`${OPT_OUT_COOKIE}=1`));
}

function writeOptOutCookie(on: boolean) {
  if (!canUseSharedCookie()) return;
  const secure = location.protocol === "https:" ? "; Secure" : "";
  const maxAge = on ? COOKIE_MAX_AGE_SEC : 0;
  document.cookie = `${OPT_OUT_COOKIE}=${on ? "1" : ""}; max-age=${maxAge}; path=/; domain=.frogswork.com; SameSite=Lax${secure}`;
}

export function isGaOptedOut(): boolean {
  try {
    if (localStorage.getItem(OPT_OUT_KEY) === "1") return true;
  } catch {
    /* private mode */
  }
  return readOptOutCookie();
}

export function setGaOptOut(optOut: boolean) {
  try {
    if (optOut) localStorage.setItem(OPT_OUT_KEY, "1");
    else localStorage.removeItem(OPT_OUT_KEY);
  } catch {
    /* ignore */
  }
  writeOptOutCookie(optOut);
}

export function processGaOptOutFromUrl() {
  const params = new URLSearchParams(location.search);
  const raw = params.get("ga_opt_out");
  if (raw === "1") setGaOptOut(true);
  else if (raw === "0") setGaOptOut(false);
  if (raw === "1" || raw === "0") {
    params.delete("ga_opt_out");
    const qs = params.toString();
    const next = `${location.pathname}${qs ? `?${qs}` : ""}${location.hash}`;
    history.replaceState(null, "", next);
  }
}

processGaOptOutFromUrl();

export function initCloudAnalytics() {
  if (!MEASUREMENT_ID || !MEASUREMENT_ID.startsWith("G-")) return;
  if (ready) return;

  if (isGaOptedOut()) {
    window[`ga-disable-${MEASUREMENT_ID}`] = true;
    return;
  }

  window.dataLayer = window.dataLayer || [];
  // gtag.js only processes `arguments` objects pushed to dataLayer; plain arrays are ignored.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", MEASUREMENT_ID, {
    anonymize_ip: true,
    send_page_view: false,
  });

  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(MEASUREMENT_ID)}`;
  s.onload = () => {
    ready = true;
  };
  document.head.appendChild(s);
}

export function trackEvent(name: string, params?: Record<string, string | number | boolean>) {
  if (!MEASUREMENT_ID || isGaOptedOut() || !window.gtag || !name) return;
  const safe: Record<string, string | number | boolean> = {};
  if (params) {
    for (const [k, v] of Object.entries(params)) {
      if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
        safe[k] = v;
      }
    }
  }
  window.gtag("event", name, safe);
}

export function trackScreen(screenName: string) {
  trackEvent("page_view", { page_title: screenName, screen_name: screenName });
}
